import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Send, RotateCcw, Sparkles } from "lucide-react";
import { streamChat, api } from "@/lib/api";
import { CHAT } from "@/constants/testIds";
import { useT } from "@/lib/i18n";
import { canUse, consume, getRemaining, isGuest } from "@/lib/guest";
import GuestBanner from "@/components/GuestBanner";
import AppellationInsight from "@/components/AppellationInsight";
import { toast } from "sonner";

function formatContent(text) {
  const html = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/^# (.+)$/gm, "<h2>$1</h2>")
    .split(/\n{2,}/)
    .map((block) => {
      if (/^[-•] /m.test(block)) {
        const items = block.split("\n").filter(Boolean).map((l) => l.replace(/^[-•]\s*/, ""));
        return `<ul>${items.map((i) => `<li>${i}</li>`).join("")}</ul>`;
      }
      if (/^\d+\.\s/m.test(block)) {
        const items = block.split("\n").filter(Boolean).map((l) => l.replace(/^\d+\.\s*/, ""));
        return `<ol>${items.map((i) => `<li>${i}</li>`).join("")}</ol>`;
      }
      if (/^<h[23]>/.test(block)) return block;
      return `<p>${block.replace(/\n/g, "<br/>")}</p>`;
    })
    .join("");
  return html;
}

export default function Chat() {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const presetQuery = params.get("q") || "";
  const urlSession = params.get("s") || null;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(urlSession);
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(getRemaining("chat"));
  const bottomRef = useRef(null);
  const sentRef = useRef(false);

  // Load existing session messages when URL has ?s=
  useEffect(() => {
    if (urlSession) {
      api.get(`/chat/sessions/${urlSession}`).then((r) => {
        setMessages(r.data.map((m) => ({ role: m.role, content: m.content })));
        setSessionId(urlSession);
      }).catch(() => setMessages([]));
    }
  }, [urlSession]);

  const sendMessage = async (text) => {
    if (!text.trim() || busy) return;
    if (!canUse("chat")) {
      setRemaining(0);
      toast.error(t("chat.error"));
      return;
    }
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", appellations: [] }]);
    setInput("");
    const c = consume("chat");
    setRemaining(c.remaining === Infinity ? null : c.remaining);

    let captured = "";
    await streamChat({
      message: text,
      sessionId,
      lang,
      onSession: (s) => {
        setSessionId(s);
        if (!urlSession) {
          // Persist session in URL without triggering navigation
          const newParams = new URLSearchParams(params);
          newParams.set("s", s);
          newParams.delete("q");
          setParams(newParams, { replace: true });
        }
      },
      onDelta: (delta) => {
        captured += delta;
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = { ...next[next.length - 1], role: "assistant", content: captured };
          return next;
        });
      },
      onAppellations: (apps) => {
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (last && last.role === "assistant") {
            next[next.length - 1] = { ...last, appellations: apps || [] };
          }
          return next;
        });
      },
      onDone: () => setBusy(false),
      onError: () => {
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = { role: "assistant", content: t("chat.error") };
          return next;
        });
        setBusy(false);
      },
    });
  };

  useEffect(() => {
    if (presetQuery && !sentRef.current) {
      sentRef.current = true;
      sendMessage(presetQuery);
    }
  }, [presetQuery]); // eslint-disable-line

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const reset = () => navigate("/");

  return (
    <div className="min-h-[calc(100dvh_-_var(--m-header-h)_-_var(--m-tabs-h))] md:min-h-screen flex flex-col">
      {/* Header */}
      <div className="sticky top-14 md:top-0 z-20 bg-white/85 backdrop-blur-xl border-b border-[#1F3A5F]/8">
        <div className="max-w-3xl mx-auto px-5 md:px-8 h-14 flex items-center justify-between">
          <div className="inline-flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#E5276A] to-[#C01661] flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <h1 className="font-display text-lg font-semibold text-[#1F3A5F]">geoVINUM AI</h1>
          </div>
          <button
            data-testid={CHAT.reset}
            onClick={reset}
            className="text-xs inline-flex items-center gap-1.5 text-[#4A5568] hover:text-[#E5276A] px-3 py-1.5 rounded-full hover:bg-[#FBF7F3] transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" /> {t("chat.reset")}
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-8 space-y-6 pb-40">
          {isGuest() && <GuestBanner feature="chat" remaining={remaining} />}
          {messages.length === 0 && (
            <div className="text-center py-16">
              <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-[#E5276A] to-[#C01661] flex items-center justify-center mb-4">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <p className="text-[#4A5568] text-sm max-w-md mx-auto">{t("chat.welcome")}</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              data-testid={m.role === "user" ? CHAT.messageUser(i) : CHAT.messageAssistant(i)}
              className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              {m.role === "assistant" && (
                <div className="shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-[#E5276A] to-[#C01661] flex items-center justify-center mt-1">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
              )}
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 prose-geo text-[15px] ${
                  m.role === "user"
                    ? "bg-[#1F3A5F] text-white"
                    : "bg-[#FBF7F3] border border-[#1F3A5F]/8 text-[#1F3A5F]"
                }`}
              >
                <div dangerouslySetInnerHTML={{ __html: formatContent(m.content || (busy && i === messages.length - 1 ? "…" : "")) }} />
                {m.role === "assistant" && m.appellations && m.appellations.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[#1F3A5F]/8 space-y-3" data-testid={`chat-appellations-${i}`}>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#E5276A] font-bold">
                      Explorer sur la carte interactive
                    </div>
                    {m.appellations.map((a) => (
                      <AppellationInsight key={a.slug || a.name} appellation={a} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Input */}
      <form onSubmit={(e) => { e.preventDefault(); sendMessage(input); }} className="sticky bottom-0 left-0 right-0 px-5 md:px-8 pb-5 pt-3 bg-gradient-to-t from-white via-white to-transparent">
        <div className="max-w-3xl mx-auto flex items-center gap-2 bg-white border border-[#1F3A5F]/15 focus-within:border-[#E5276A] rounded-2xl px-4 py-2.5 shadow-md transition-colors">
          <input
            data-testid={CHAT.input}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t("chat.placeholder")}
            disabled={busy}
            className="flex-1 bg-transparent outline-none text-base placeholder:text-[#8B95A1] text-[#1F3A5F] px-2 py-2 disabled:opacity-50"
          />
          <button
            data-testid={CHAT.send}
            type="submit"
            disabled={busy || !input.trim()}
            className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#E5276A] hover:bg-[#C01661] text-white disabled:opacity-40 transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <div className="max-w-3xl mx-auto mt-2 text-center text-[10px] text-[#8B95A1]">{t("home.disclaimer")}</div>
      </form>
    </div>
  );
}
